import {
  Suspense,
  useCallback,
  useEffect,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { HeadContent, Outlet, Scripts, createRootRoute, useMatches } from "@tanstack/react-router";
import siteStyles from "../styles/site.css?url";
import themeStyles from "../styles/theme.css?url";
import demoCheckoutStyles from "../styles/demo-checkout.css?url";
import { readyLeagueParts } from "../components/league/lazy-widgets";
import platformStyles from "../styles/platform.css?url";
import { ErrorState } from "../components/error-state";
import { SiteFooter } from "../components/site-footer";
import { SiteHeader } from "../components/site-header";
import { OwnerBar } from "../components/owner-bar";
import { styleDeclarations } from "../lib/css";
import { errorHead, notFoundHead } from "../lib/error-head";
import { markHydrated } from "../lib/hydrated";
import { readyLazy, readyOnServer } from "../lib/ready-lazy";
import {
  useAuthReturnLinks,
  useNavigationPending,
  useSiteNavigation,
  useSiteViews,
} from "../lib/navigation";
import { loadSiteShell, type SiteShell } from "../server/shell";
import { isPlatformShell, type PlatformShell } from "../platform/shell";
import { RegularPlanContext } from "../platform/plan-context";
import { isTemplatePreviewShell, type TemplatePreviewShell } from "../lib/template-preview-shell";
import type { TemplatePreview } from "../server/template-preview.server";
import { TemplatePreviewHeadContent } from "../components/template-preview/head-content";
import { PublishedShellContext, usePublishedViewer } from "../lib/published-viewer";
import { publishedPresentationPath } from "../../../shared/src/published-site";
import { sitePage } from "../lib/links";

// Only a prospect demo's test checkout shows this, so other sites never load its code.
const DemoCheckoutDialog = readyLazy(() =>
  import("../components/membership/demo-checkout-dialog").then((module) => ({
    default: module.DemoCheckoutDialog,
  })),
);

// A website with a popup loads its code once the page is up; others never do.
const SitePopup = readyLazy(() =>
  import("../components/site-popup").then((module) => ({ default: module.SitePopup })),
);

// Drate's own header and footer load only on the platform host, never on creator sites.
const PlatformChrome = readyLazy(() => import("../platform/chrome"));

/**
 * The creator website's document and persistent layout. The shell (header, footer, theme, menu)
 * loads once per document; moving between pages keeps it mounted and requests only the next
 * page's data.
 */
export const Route = createRootRoute({
  loader: async ({ location }) => {
    const shell = await loadSiteShell({ data: location.pathname });
    // Drate's own pages render whole on the server, header and footer included (lib/ready-lazy.ts).
    if (isPlatformShell(shell)) await readyOnServer(PlatformChrome);
    else if ("demoCheckout" in shell && shell.demoCheckout) await readyOnServer(DemoCheckoutDialog);
    return shell;
  },
  staleTime: Infinity,
  gcTime: Infinity,
  shouldReload: false,
  head: ({ loaderData: shell, matches }) => {
    // A template's example page writes its own head (components/template-preview/head.ts).
    if (isTemplatePreviewShell(shell)) return {};
    const head = isPlatformShell(shell)
      ? platformHead(
          shell,
          matches.some(
            ({ routeId }: { routeId: string }) =>
              routeId === "/" || routeId.startsWith("/use-cases"),
          ),
        )
      : creatorHead(shell);
    // A missing or failed page has no head of its own; it is described as a 404 or a 500, and
    // never indexed.
    const state = matches.some((match) => match.status === "notFound" || match._notFound)
      ? notFoundHead
      : matches.some((match) => match.status === "error")
        ? errorHead
        : null;
    if (!state) return head;
    const page = state(shell);
    return {
      ...head,
      meta: [...head.meta, ...(page.meta ?? [])],
      links: [...head.links, ...(page.links ?? [])],
      scripts: [...("scripts" in head ? head.scripts : []), ...(page.scripts ?? [])],
    };
  },
  shellComponent: Document,
  component: Layout,
  notFoundComponent: NotFound,
  errorComponent: ErrorPage,
});

/** A creator site's document head: its favicon, theme color, faces and stylesheets. */
function creatorHead(shell: SiteShell | undefined) {
  return {
    meta: [
      { charSet: "UTF-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1.0, viewport-fit=cover" },
      ...(shell ? [{ name: "theme-color", content: shell.themeColor }] : []),
    ],
    links: [
      ...(shell
        ? [
            {
              rel: "icon",
              href: shell.creator.faviconUrl || shell.creator.logoUrl || "/favicon.svg",
            },
            // A phone's home screen takes the creator's own icon when they chose one.
            ...(shell.creator.faviconUrl
              ? [{ rel: "apple-touch-icon", href: shell.creator.faviconUrl }]
              : []),
            // Each theme's display face, and faces chosen in Styles, load before the page asks.
            ...fontPreloads(shell),
            ...(shell.podcastFeed
              ? [
                  {
                    rel: "alternate",
                    type: "application/rss+xml",
                    title: shell.podcastFeed.title,
                    href: shell.podcastFeed.url,
                  },
                ]
              : []),
          ]
        : []),
      { rel: "stylesheet", href: siteStyles },
      ...(shell?.demoCheckout ? [{ rel: "stylesheet", href: demoCheckoutStyles }] : []),
      // React groups stylesheets by precedence, in the order it first meets each group: routes'
      // own styles join the default group, so the themes still come after them.
      { rel: "stylesheet", href: themeStyles, precedence: "theme" },
    ],
    styles: shell?.fonts.faceCss ? [{ children: shell.fonts.faceCss }] : [],
    // Marks each picture as it arrives, so its space can show a color until then.
    scripts: shell && shell.pictureLoading !== "none" ? [{ children: pictureArrivalScript }] : [],
  };
}

/**
 * Marks every picture `data-arrived` once it has loaded (or failed), from the first one the page
 * parses, so CSS shows a color or shimmer in its space until then, or fades it in (site-layout.css).
 * A picture there before the first frame (`"early"`, from the cache) shows at once. It sets
 * `data-pictures` first: without it, as when scripts don't run, every picture shows at once.
 */
const pictureArrivalScript = `(()=>{const d=document;let p=false;requestAnimationFrame(()=>{p=true});const a=i=>i.hasAttribute("data-arrived")||i.setAttribute("data-arrived",p?"":"early"),m=e=>{e.target&&e.target.tagName==="IMG"&&a(e.target)};d.documentElement.setAttribute("data-pictures","");d.addEventListener("load",m,true);d.addEventListener("error",m,true);d.addEventListener("DOMContentLoaded",()=>{for(const i of d.images)i.complete&&a(i)})})()`;

/**
 * Drate's own pages' document head: Drate's icons, General
 * Sans first, the dashboard's design tokens, and the appearance script that sets light or dark
 * before anything paints. Each page adds its own title and description.
 */
function platformHead(shell: PlatformShell, inter: boolean) {
  return {
    meta: [
      { charSet: "UTF-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1.0, viewport-fit=cover" },
      // The appearance script sets the dark color before paint; light pages use this one.
      { name: "theme-color", content: "#fafaf9" },
    ],
    links: [
      { rel: "icon", href: "/favicon.ico", sizes: "32x32" },
      { rel: "icon", href: "/favicon.svg", type: "image/svg+xml" },
      { rel: "apple-touch-icon", href: "/apple-touch-icon.png" },
      // Guide covers, the homepage film and the Learn menu's pictures come from the CDN.
      { rel: "preconnect", href: "https://cdn.drate.app" },
      // Drate's pages set every word in General Sans. Inter fills the creator-site mocks and the
      // ↗ of links to example sites (General Sans and its Arial fallback have no ↗). The homepage
      // and the use-case pages show those in their first screen, so they preload it at low
      // priority; on other pages it only took bandwidth from the page's own text and hero.
      {
        rel: "preload",
        href: "/_app/fonts/general-sans-variable.woff2",
        as: "font",
        type: "font/woff2",
        crossOrigin: "anonymous" as const,
      },
      ...(inter
        ? [
            {
              rel: "preload",
              href: "/_app/fonts/inter-variable.woff2",
              as: "font",
              type: "font/woff2",
              crossOrigin: "anonymous" as const,
              fetchPriority: "low" as const,
            },
          ]
        : []),
      { rel: "stylesheet", href: siteStyles },
      { rel: "stylesheet", href: platformStyles },
      { rel: "stylesheet", href: themeStyles, precedence: "theme" },
    ],
    scripts: [{ children: shell.appearanceScript }],
  };
}

function fontPreloads(shell: SiteShell) {
  const files = [
    shell.fonts.heading,
    shell.fonts.body !== shell.fonts.heading ? shell.fonts.body : undefined,
    // Inter is the default body face; a site with its own body face never asks for it here.
    shell.fonts.body ? undefined : "/_app/fonts/inter-variable.woff2",
  ];
  return files
    .filter((file): file is string => !!file)
    .map((href) => ({
      rel: "preload",
      href,
      as: "font",
      // A creator's uploaded font may be any of the formats they could upload.
      ...(href.startsWith("/api/media/") ? {} : { type: "font/woff2" }),
      crossOrigin: "anonymous" as const,
    }));
}

function Document({ children }: { children: ReactNode }) {
  const loaded = Route.useLoaderData() as
    | SiteShell
    | PlatformShell
    | TemplatePreviewShell
    | undefined;
  if (isTemplatePreviewShell(loaded))
    return <TemplatePreviewDocument>{children}</TemplatePreviewDocument>;
  if (isPlatformShell(loaded))
    return <PlatformDocument shell={loaded}>{children}</PlatformDocument>;
  const shell = loaded;
  return (
    <html
      lang={shell?.language ?? "en"}
      data-playback-creator={shell?.creator.id}
      data-playback-user={shell?.viewer.userId ?? undefined}
      data-page-transitions={shell?.pageTransitions ? "on" : "off"}
      data-picture-loading={shell?.pictureLoading}
      data-theme={shell?.theme}
      data-header-style={shell?.header.style}
      data-header-sticky={shell ? String(shell.header.sticky) : undefined}
      data-header-size={shell?.header.size}
      data-header-floating={shell?.header.floating ? "" : undefined}
      data-header-shadow={shell?.header.shadow ? "" : undefined}
      data-footer-style={shell?.footer.style}
      data-footer-align={shell?.footer.alignment}
      data-footer-separator={shell && !shell.footer.separator ? "none" : undefined}
      data-footer-width={shell?.footer.fullWidth ? "full" : undefined}
      data-footer-tone={shell?.footer.tone}
      data-presentation={shell?.presentation}
      data-spotlight={shell?.header.spotlight ? "" : undefined}
      data-site-views={shell?.countViews ? "" : undefined}
      // The router reads this before hydrating to choose how long pages stay in memory.
      data-viewer={
        shell?.publication
          ? "published"
          : shell && !shell.viewer.signedIn
            ? "anonymous"
            : "signed-in"
      }
      data-publication={shell?.publication ? "" : undefined}
      style={styleDeclarations(shell?.style)}
    >
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

/** Drate's own pages' document; the appearance script adds data-appearance before hydration. */
function PlatformDocument({ shell, children }: { shell: PlatformShell; children: ReactNode }) {
  return (
    <html
      lang="en"
      data-page-transitions="on"
      data-platform=""
      data-site-views={shell.countViews ? "" : undefined}
      // The router reads this before hydrating to choose how long pages stay in memory.
      data-viewer={shell.viewer.signedIn ? "signed-in" : "anonymous"}
      suppressHydrationWarning
    >
      <head>
        <HeadContent />
      </head>
      <body>
        <RegularPlanContext.Provider value={shell.regular}>{children}</RegularPlanContext.Provider>
        <Scripts />
      </body>
    </html>
  );
}

/**
 * A template's example page (/use-cases/<slug>/preview): its own document and theme, drawn on the
 * server with no client app, as the Astro preview was. Its links open whole pages.
 */
function TemplatePreviewDocument({ children }: { children: ReactNode }) {
  const preview = useMatches({
    select: (matches) =>
      matches.find(
        (match) => (match.loaderData as { kind?: string } | undefined)?.kind === "template-preview",
      )?.loaderData as TemplatePreview | undefined,
  });
  return (
    <html
      lang="en"
      data-theme={preview?.theme}
      data-header-style={preview?.header?.style}
      data-header-sticky={preview?.header ? String(preview.header.sticky) : undefined}
      data-header-size={preview?.header?.size}
      style={
        preview?.accent || preview?.style
          ? ({
              ...styleDeclarations(preview.style),
              ...(preview.accent ? { "--accent": preview.accent } : {}),
            } as CSSProperties)
          : undefined
      }
    >
      <head>
        <TemplatePreviewHeadContent />
      </head>
      <body>{children}</body>
    </html>
  );
}

function Layout() {
  const shell = Route.useLoaderData();
  if (isTemplatePreviewShell(shell)) return <Outlet />;
  return isPlatformShell(shell) ? (
    <Suspense fallback={null}>
      <PlatformChrome shell={shell}>
        <Outlet />
      </PlatformChrome>
    </Suspense>
  ) : (
    <CreatorLayout shell={shell} />
  );
}

function CreatorLayout({ shell: initialShell }: { shell: SiteShell }) {
  const shell = usePublishedViewer(initialShell);
  const page = useCallback(
    (path: string) =>
      sitePage(path) &&
      (!shell.publication ||
        (publishedPresentationPath(path) &&
          (!shell.publication.viewerPaths.includes(path) ||
            shell.publication.enhancedPaths?.includes(path)))),
    [shell.publication],
  );
  // An owner's preview loads whole documents, so its draft never enters client navigation; so do
  // links out of a minimal document (checkout), which opens the full site afresh.
  useSiteNavigation({
    enabled: !shell.preview && !shell.minimal && (!shell.publishedDelivery || !!shell.publication),
    prefetch: !shell.viewer.signedIn && !shell.preview && !shell.minimal,
    page,
  });
  useNavigationPending();
  useSiteViews(shell.countViews);
  useAuthReturnLinks();
  // The popup mounts after hydration, so no page waits for it or carries it in its HTML.
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    markHydrated();
    setHydrated(true);
    void import("../../src/lib/league-tooltip-client").then(({ initializeLeagueTooltips }) =>
      initializeLeagueTooltips(),
    );
    if (shell.league) void readyLeagueParts();
  }, []);
  return (
    <PublishedShellContext.Provider value={shell}>
      {/* The audio player that keeps playing while visitors move between pages. */}
      <drate-audio-host />
      {shell.backdrop && (
        <div className="creator-backdrop" aria-hidden="true">
          <img
            src={shell.backdrop}
            alt=""
            decoding="async"
            fetchPriority="low"
            style={{ opacity: shell.backdropOpacity, objectPosition: shell.backdropPosition }}
          />
          <span />
        </div>
      )}
      {/* First in the page, so the owner's tools are the first stops for the keyboard. */}
      {shell.owner &&
        (shell.ownerWebsiteView ? (
          // The dashboard's Website view shows where the site stands from this.
          <template
            data-owner-state={
              !shell.owner.published ? "private" : shell.owner.changed ? "changed" : "live"
            }
          />
        ) : (
          <OwnerBar owner={shell.owner} />
        ))}
      <SiteHeader shell={shell} />
      <main id="main">
        <Outlet />
      </main>
      <SiteFooter shell={shell} />
      {shell.popup && hydrated && (
        <Suspense>
          <SitePopup
            popup={shell.popup}
            storageKey={`drate-popup:${shell.creator.id}`}
            creatorName={shell.creator.name}
          />
        </Suspense>
      )}
      {shell.demoCheckout && (
        <Suspense>
          <DemoCheckoutDialog
            creatorName={shell.creator.name}
            plans={shell.demoCheckout.plans}
            stripe={shell.demoCheckout.stripe}
          />
        </Suspense>
      )}
    </PublishedShellContext.Provider>
  );
}

function NotFound() {
  const shell = Route.useLoaderData() as
    | SiteShell
    | PlatformShell
    | TemplatePreviewShell
    | undefined;
  return (
    <ErrorState
      status={404}
      creatorName={
        (!isPlatformShell(shell) && !isTemplatePreviewShell(shell) && shell?.creator.name) ||
        "Drate"
      }
    />
  );
}

function ErrorPage() {
  const shell = Route.useLoaderData() as
    | SiteShell
    | PlatformShell
    | TemplatePreviewShell
    | undefined;
  return (
    <ErrorState
      status={500}
      creatorName={
        (!isPlatformShell(shell) && !isTemplatePreviewShell(shell) && shell?.creator.name) ||
        "Drate"
      }
    />
  );
}
